import React, { useEffect, useState, useCallback } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import SearchBar from '../../components/common/SearchBar';
import Pagination from '../../components/common/Pagination';
import Modal from '../../components/common/Modal';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import StatusBadge from '../../components/common/StatusBadge';
import ToastStack, { useToasts } from '../../components/common/Toast';
import { useAuth } from '../../context/AuthContext';
import { getMemberList, updateMember, withdrawMember } from '../../api/member';
import { formatDateTime } from '../../utils/format';

const STATUS_OPTIONS = [
  { value: '', label: '전체 상태' },
  { value: 'NORMAL', label: '정상' },
  { value: 'WITHDRAWN', label: '탈퇴' },
  { value: 'DORMANT', label: '휴면' },
];

export default function MemberListPage() {
  const { auth } = useAuth();
  const [condition, setCondition] = useState({ pageNo: 1, pageSize: 10, memberStatus: '' });
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null);
  const [withdrawTarget, setWithdrawTarget] = useState(null);
  const { toasts, pushToast, removeToast } = useToasts();

  const fetchList = useCallback(async (cond) => {
    setLoading(true);
    setError('');
    try {
      const res = await getMemberList(cond);
      setPage(res.data);
    } catch (err) {
      setError(err.response?.data?.message || '회원 목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchList(condition); }, [condition, fetchList]);

  const handleSearch = (partial) => setCondition((c) => ({ ...c, ...partial }));
  const handlePageChange = (pageNo) => setCondition((c) => ({ ...c, pageNo }));

  const handleSaveEdit = async (form) => {
    try {
      await updateMember(editing.memberId, { ...form, updatedBy: auth.adminName });
      setEditing(null);
      fetchList(condition);
    } catch (err) {
      pushToast(err.response?.data?.message || '회원 정보 수정에 실패했습니다.', 'error');
    }
  };

  const handleWithdraw = async () => {
    try {
      await withdrawMember(withdrawTarget.memberId, auth.adminName);
      setWithdrawTarget(null);
      fetchList(condition);
    } catch (err) {
      pushToast(err.response?.data?.message || '탈퇴 처리에 실패했습니다.', 'error');
    }
  };

  return (
    <AdminLayout title="회원관리" menuUrl="/admin/member">
      <ToastStack toasts={toasts} onDismiss={removeToast} />

      {/* 타이틀 영역 */}
      <div className="page-header">
        <div>
          <h1>회원관리</h1>
          <div className="subtitle">회원 정보를 조회, 수정하고 탈퇴 처리할 수 있습니다.</div>
        </div>
      </div>

      {/* 헤더 영역: 검색(상태필터+계정) + 건수 */}
      <div className="list-toolbar">
        <SearchBar
          extraFilter={{ options: STATUS_OPTIONS, value: condition.memberStatus, onChange: (v) => handleSearch({ memberStatus: v, pageNo: 1 }) }}
          placeholder="계정으로 검색"
          onSearch={handleSearch}
        />
      </div>

      <div className="list-count-bar">
        <span className="list-count">전체 <b>{page?.totalCount ?? 0}</b>건</span>
      </div>

      <div className="card">
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>계정</th>
                <th>이름</th>
                <th>연락처</th>
                <th>이메일</th>
                <th>상태</th>
                <th>가입일</th>
                <th>관리</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={7} className="empty-row">불러오는 중...</td></tr>}
              {!loading && error && <tr><td colSpan={7} className="empty-row error-state">{error}</td></tr>}
              {!loading && !error && page?.list?.length === 0 && (
                <tr><td colSpan={7} className="empty-row">등록된 회원이 없습니다.</td></tr>
              )}
              {!loading && !error && page?.list?.map((m) => (
                <tr key={m.memberId}>
                  <td>{m.memberAccount}</td>
                  <td>{m.memberName}</td>
                  <td>{m.phoneNo}</td>
                  <td>{m.email}</td>
                  <td><StatusBadge value={m.memberStatus} /></td>
                  <td>{formatDateTime(m.createdAt)}</td>
                  <td className="actions">
                    <button className="btn btn-secondary btn-sm" onClick={() => setEditing(m)}>수정</button>
                    {m.memberStatus !== 'WITHDRAWN' && (
                      <button className="btn btn-danger btn-sm" onClick={() => setWithdrawTarget(m)}>탈퇴처리</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {page && <Pagination {...page} onChange={handlePageChange} />}

      {editing && (
        <MemberEditModal member={editing} onClose={() => setEditing(null)} onSave={handleSaveEdit} />
      )}

      {withdrawTarget && (
        <ConfirmDialog
          title="회원 탈퇴 처리"
          message={`${withdrawTarget.memberAccount} 님을 탈퇴 처리하시겠습니까?`}
          confirmLabel="탈퇴 처리"
          danger
          onCancel={() => setWithdrawTarget(null)}
          onConfirm={handleWithdraw}
        />
      )}
    </AdminLayout>
  );
}

function MemberEditModal({ member, onClose, onSave }) {
  const [form, setForm] = useState({
    memberName: member.memberName || '',
    phoneNo: member.phoneNo || '',
    email: member.email || '',
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(form);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title="회원 정보 수정"
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose}>취소</button>
          <button className="btn btn-primary" onClick={submit} disabled={saving}>{saving ? '저장 중...' : '저장'}</button>
        </>
      }
    >
      <form onSubmit={submit}>
        <div className="field">
          <label>계정</label>
          <input className="input" value={member.memberAccount} disabled />
        </div>
        <div className="field">
          <label>이름</label>
          <input className="input" value={form.memberName} onChange={(e) => setForm({ ...form, memberName: e.target.value })} />
        </div>
        <div className="field">
          <label>연락처</label>
          <input className="input" value={form.phoneNo} onChange={(e) => setForm({ ...form, phoneNo: e.target.value })} />
        </div>
        <div className="field">
          <label>이메일</label>
          <input className="input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </div>
      </form>
    </Modal>
  );
}
